import { useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import {
  Chip,
  ColorsV2,
  Flex,
  Table,
  TableContainer,
  Text,
  spaces,
} from '@electricjs/arc';

import ApplicationIcon from '@/components/Applications/ApplicationIcon';
import TableCellFlexContainer from 'common/Table/TableCellFlexContainer';
import { ExternalAppRow } from './types';

type ExternalAppsTableProps = {
  rows: ExternalAppRow[];
  tableId: string;
  sectionHeader: string;
  applicationColumnHeader: string;
};

const columnHelper = createColumnHelper<ExternalAppRow>();

const ExternalAppsTable = ({
  rows,
  tableId,
  sectionHeader,
  applicationColumnHeader,
}: ExternalAppsTableProps) => {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'application',
        header: applicationColumnHeader,
        cell: ({ row }) => (
          <TableCellFlexContainer>
            <Flex vAlignContent="center" gap="1rem">
              <ApplicationIcon
                name={row.original.appName}
                icon={row.original.appIcon}
                size="3rem"
              />
              <Flex flexDirection="column">
                <Text variant="body-2">{row.original.appName}</Text>
                <Flex gap="0.5rem" mt={spaces.space_2xs_rem} flexWrap="wrap">
                  {row.original.groups.map(group => (
                    <Chip
                      key={group.id}
                      id={`external-group-chip-${row.original.appId}-${group.id}`}
                      variant="small"
                      backgroundColor={ColorsV2.GRAY_LIGHTEST}>
                      <Text variant="legal">{group.name}</Text>
                    </Chip>
                  ))}
                </Flex>
              </Flex>
            </Flex>
          </TableCellFlexContainer>
        ),
        meta: { flex: '1' },
      }),
    ],
    [applicationColumnHeader]
  );

  return (
    <TableContainer flexDirection="column" width="100%">
      <Flex px="2rem" py={spaces.space_m_rem}>
        <Text variant="heading-3">{sectionHeader}</Text>
      </Flex>
      <Table
        id={tableId}
        columns={columns}
        data={rows}
        loading={false}
        emptyStateText=""
      />
    </TableContainer>
  );
};

export { ExternalAppsTable };
